/**
 * Checklist API Contract
 *
 * Mirrors: backend/src/routes/checklist.routes.ts
 * 1:1 parity with existing Express routes
 *
 * SECURITY: companyId comes from JWT context, never from request
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { ChecklistAssignmentSchema, ChecklistPrioritySchema, ChecklistResponseSchema, ChecklistSourceTypeSchema, } from "@hourly/api/checklist";
// ===== CONTRACT-SPECIFIC INPUT SCHEMAS (WITHOUT companyId) =====
/**
 * Create checklist input - omits companyId (from JWT)
 */
const CreateChecklistContractInputSchema = z.object({
    name: z.string().trim().min(1).max(100),
    items: z
        .array(z.object({
        description: z.string().trim().min(1).max(500),
    }))
        .min(1),
    assignment: ChecklistAssignmentSchema,
    requiresPhoto: z.boolean().optional(),
    priority: ChecklistPrioritySchema.optional(),
    dueDate: z.iso.datetime().optional(),
    sourceTemplateId: z.uuid().optional(),
});
/**
 * Create checklist from alert input - omits companyId (from JWT)
 */
const CreateChecklistFromAlertContractInputSchema = z.object({
    alertInstanceId: z.uuid(), // from path
    name: z.string().trim().min(1).max(100),
    items: z
        .array(z.object({
        description: z.string().trim().min(1).max(500),
    }))
        .min(1),
    requiresPhoto: z.boolean().optional(),
    priority: ChecklistPrioritySchema.optional(),
});
/**
 * Update checklist input
 */
const UpdateChecklistContractInputSchema = z.object({
    id: z.uuid(), // from path
    name: z.string().trim().min(1).max(100).optional(),
    priority: ChecklistPrioritySchema.optional(),
    dueDate: z.iso.datetime().nullable().optional(),
    requiresPhoto: z.boolean().optional(),
    checklistCompleted: z.boolean().optional(),
});
/**
 * Get checklist by ID input
 */
const GetChecklistContractInputSchema = z.object({
    id: z.uuid(),
});
/**
 * Get checklists query input - omits companyId (from JWT)
 */
const GetChecklistsContractQuerySchema = z.object({
    shiftId: z.uuid().optional(),
    employmentId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    completed: z.boolean().optional(),
    priority: ChecklistPrioritySchema.optional(),
    sourceType: ChecklistSourceTypeSchema.optional(),
});
/**
 * Get my checklists query input - omits companyId (from JWT)
 */
const GetMyChecklistsContractQuerySchema = z.object({
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    completed: z.boolean().optional(),
});
/**
 * Delete checklist input
 */
const DeleteChecklistContractInputSchema = z.object({
    id: z.uuid(),
});
/**
 * Complete checklist item input
 */
const CompleteChecklistItemContractInputSchema = z.object({
    id: z.uuid(), // checklist ID from path
    itemId: z.uuid(), // item ID from path
});
/**
 * Uncomplete checklist item input
 */
const UncompleteChecklistItemContractInputSchema = z.object({
    id: z.uuid(), // checklist ID from path
    itemId: z.uuid(), // item ID from path
});
/**
 * Add checklist photo input
 */
const AddChecklistPhotoContractInputSchema = z.object({
    id: z.uuid(), // checklist ID from path
    fileUploadId: z.uuid(),
});
/**
 * Remove checklist photo input
 */
const RemoveChecklistPhotoContractInputSchema = z.object({
    id: z.uuid(), // checklist ID from path
    photoId: z.uuid(), // photo ID from path
});
/**
 * Reassign checklist input
 */
const ReassignChecklistContractInputSchema = z.object({
    id: z.uuid(), // checklist ID from path
    assignment: ChecklistAssignmentSchema,
});
// ===== CONTRACTS =====
/**
 * POST /checklists
 * Create a new checklist
 */
export const createChecklist = oc
    .route({
    method: "POST",
    path: "/checklists",
    summary: "Create a new checklist",
    tags: ["Checklists"],
    successStatus: 201,
})
    .input(CreateChecklistContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * POST /checklists/from-alert/{alertInstanceId}
 * Create a checklist from an alert (de-escalation)
 */
export const createChecklistFromAlert = oc
    .route({
    method: "POST",
    path: "/checklists/from-alert/{alertInstanceId}",
    summary: "Create a checklist from an alert",
    tags: ["Checklists"],
    successStatus: 201,
})
    .input(CreateChecklistFromAlertContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * GET /checklists/{id}
 * Get a checklist by ID
 */
export const getChecklist = oc
    .route({
    method: "GET",
    path: "/checklists/{id}",
    summary: "Get a checklist by ID",
    tags: ["Checklists"],
})
    .input(GetChecklistContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * GET /checklists
 * List checklists with filters
 */
export const getChecklists = oc
    .route({
    method: "GET",
    path: "/checklists",
    summary: "List checklists with filters",
    tags: ["Checklists"],
})
    .input(GetChecklistsContractQuerySchema)
    .output(z.array(ChecklistResponseSchema));
/**
 * GET /checklists/my
 * Get checklists for current user
 */
export const getMyChecklists = oc
    .route({
    method: "GET",
    path: "/checklists/my",
    summary: "Get checklists for current user",
    tags: ["Checklists"],
})
    .input(GetMyChecklistsContractQuerySchema)
    .output(z.array(ChecklistResponseSchema));
/**
 * PATCH /checklists/{id}
 * Update a checklist
 */
export const updateChecklist = oc
    .route({
    method: "PATCH",
    path: "/checklists/{id}",
    summary: "Update a checklist",
    tags: ["Checklists"],
})
    .input(UpdateChecklistContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * DELETE /checklists/{id}
 * Delete a checklist
 */
export const deleteChecklist = oc
    .route({
    method: "DELETE",
    path: "/checklists/{id}",
    summary: "Delete a checklist",
    tags: ["Checklists"],
    successStatus: 204,
})
    .input(DeleteChecklistContractInputSchema)
    .output(z.void());
/**
 * POST /checklists/{id}/items/{itemId}/complete
 * Complete a checklist item
 */
export const completeChecklistItem = oc
    .route({
    method: "POST",
    path: "/checklists/{id}/items/{itemId}/complete",
    summary: "Complete a checklist item",
    tags: ["Checklists"],
})
    .input(CompleteChecklistItemContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * POST /checklists/{id}/items/{itemId}/uncomplete
 * Uncomplete a checklist item
 */
export const uncompleteChecklistItem = oc
    .route({
    method: "POST",
    path: "/checklists/{id}/items/{itemId}/uncomplete",
    summary: "Uncomplete a checklist item",
    tags: ["Checklists"],
})
    .input(UncompleteChecklistItemContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * POST /checklists/{id}/photos
 * Add a photo to a checklist
 */
export const addChecklistPhoto = oc
    .route({
    method: "POST",
    path: "/checklists/{id}/photos",
    summary: "Add a photo to a checklist",
    tags: ["Checklists"],
})
    .input(AddChecklistPhotoContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * DELETE /checklists/{id}/photos/{photoId}
 * Remove a photo from a checklist
 */
export const removeChecklistPhoto = oc
    .route({
    method: "DELETE",
    path: "/checklists/{id}/photos/{photoId}",
    summary: "Remove a photo from a checklist",
    tags: ["Checklists"],
})
    .input(RemoveChecklistPhotoContractInputSchema)
    .output(ChecklistResponseSchema);
/**
 * POST /checklists/{id}/reassign
 * Reassign a checklist to a different entity
 */
export const reassignChecklist = oc
    .route({
    method: "POST",
    path: "/checklists/{id}/reassign",
    summary: "Reassign a checklist to a different entity",
    tags: ["Checklists"],
})
    .input(ReassignChecklistContractInputSchema)
    .output(ChecklistResponseSchema);
// ===== EXPORTED CONTRACT =====
export const checklistContract = {
    create: createChecklist,
    createFromAlert: createChecklistFromAlert,
    get: getChecklist,
    list: getChecklists,
    my: getMyChecklists,
    update: updateChecklist,
    delete: deleteChecklist,
    completeItem: completeChecklistItem,
    uncompleteItem: uncompleteChecklistItem,
    addPhoto: addChecklistPhoto,
    removePhoto: removeChecklistPhoto,
    reassign: reassignChecklist,
};
//# sourceMappingURL=checklist.js.map